import BookIndex from "components/shared/book/list";
import { Transition } from "components/shared/column";
import SharedReactTable from "components/shared/reactTable";
import { legalPersonFlags, legalPersonFlagsEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { isDeletedColumn, SelectColumnFilter } from "helpers/reactTable";
import { deletedFilters, tableContainer } from "helpers/redux";
import { enumLabel, flagVal } from "helpers/system";
import React from "react";
import { Link } from "react-router-dom";
import { deleteItem, restore, setMain, unsetMain } from "sources/books/legalPersons";

const mainFlagVal = flagVal(legalPersonFlags, legalPersonFlagsEnum.Main);

class LegalPersons extends BookIndex {
    state = { tableColumns: this.tableColumns };

    componentWillUnmount() {
        this.props.clearParams(deletedFilters());
    }

    componentDidUpdate(prevProps) {
        if (prevProps.canEditBooks !== this.props.canEditBooks) {
            this.setState({ tableColumns: this.tableColumns });
        }
    }

    get tableColumns() {
        return [
            { Header: "ID", accessor: "id", width: 100, filterable: true, filterType: "int" },
            {
                Header: "Название",
                accessor: "name",
                filterable: true,
                Cell: ({ value, row }) => {
                    const isMain = (row.original.flags & mainFlagVal) === mainFlagVal;
                    return (
                        <>
                            <Link to={paths.legalPersonsShow(row.original.id)}>{value}</Link>
                            {isMain && <span className="badge badge-primary ml-2">Основная</span>}
                        </>
                    );
                },
            },
            { Header: "ИНН", accessor: "inn", filterable: true },
            {
                Header: "Тип НДС",
                accessor: "ndsTypeId",
                filterable: true,
                filterType: "select",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            inputProps: { name: "ndsTypeId" },
                            dictionary: "nDSType",
                            options: this.props.enums.nDSType,
                        }}
                    />
                ),
                Cell: ({ value }) => enumLabel(this.props.enums.nDSType, value),
                CSVExport: ({ value }) => enumLabel(this.props.enums.nDSType, value),
            },
            {
                Header: "Тип оплаты",
                accessor: "paymentTypeId",
                filterable: true,
                filterType: "select",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            inputProps: { name: "paymentTypeId" },
                            options: this.props.enums.paymentType,
                        }}
                    />
                ),
                Cell: ({ value }) => enumLabel(this.props.enums.paymentType, value),
                CSVExport: ({ value }) => enumLabel(this.props.enums.paymentType, value),
            },
            isDeletedColumn(this.restore, this.props.canEditBooks),
            {
                Header: "Действие",
                width: 190,
                notExportable: true,
                id: "action",
                sortable: false,
                Cell: ({ row }) => this.actions(row.original),
            },
        ];
    }

    actions = (data) => (
        <>
            {!data.isDeleted && (
                (data.flags & mainFlagVal) === mainFlagVal ? (
                    <div
                        className="btn btn-xs btn-primary mr-2"
                        onClick={this.unsetMain.bind(this, data)}
                    >
                        Убрать статус основной
                    </div>
                ) : (
                    <div
                        className="btn btn-xs btn-primary mr-2"
                        onClick={this.setMain.bind(this, data)}
                    >
                        Сделать основной
                    </div>
                )
            )}
            <Link className="btn btn-xs btn-outline-primary mr-2" to={paths.legalPersonsShow(data.id)}>
                Изменить
            </Link>
            {!data.isDeleted && (
                <div className="btn btn-xs btn-outline-danger" onClick={this.deleteItem.bind(this, data)}>
                    Удалить
                </div>
            )}
        </>
    );

    deleteItem = (data) => {
        const { modal, mergeParams } = this.props;
        return modal({
            name: "confirm",
            title: "Удаление организации",
            text: "Вы уверены, что хотите удалить организацию?",
            buttons: { confirm: "Удалить", cancel: "Отменить" },
            action: () => deleteItem({ id: data.id }).then(() => mergeParams({})),
        });
    };

    setMain = (data) => {
        const { modal, mergeParams } = this.props;
        return modal({
            name: "confirm",
            title: "Вы уверены, что хотите сделать организацию основной?",
            buttons: { confirm: "Да", cancel: "Нет" },
            action: () => setMain({ id: data.id }).then(() => mergeParams({})),
        });
    };

    unsetMain = (data) => {
        const { modal, mergeParams } = this.props;
        return modal({
            name: "confirm",
            title: "Вы уверены, что хотите снять статус основной с организации?",
            buttons: { confirm: "Да", cancel: "Нет" },
            action: () => unsetMain({ id: data.id }).then(() => mergeParams({})),
        });
    };

    restore = (id) => {
        this.props.modal({
            name: "confirm",
            title: "Вы действительно хотите восстановить?",
            action: () => restore({ id }).then(() => this.props.mergeParams(deletedFilters())),
            buttons: { confirm: "Да", cancel: "Нет" },
        });
    };

    // editItem = data => {
    //     const { modal, mergeParams } = this.props;
    //     return modal({
    //         name: "default",
    //         windowMod: "wide",
    //         title: "Изменение организации",
    //         CustomBody: LegalPersonForm,
    //         bodyData: { callback: () => mergeParams({}), enums: this.props.enums, id: data.id },
    //     });
    // };

    renderHead = () => (
        <div className="book-list__head">
            <div className="book-list__head-close close close--sm" onClick={this.close} />
            <div className="book-list__head-title">
                Организации контрагентов
                {this.renderCounter()}
            </div>
            <div className="btn-toolbar">{this.renderCreateButton()}</div>
        </div>
    );

    renderListComponent = () => <SharedReactTable {...this.props} columns={this.state.tableColumns} />;

    renderCreateButton = () => (
        <Link className="btn btn-brand btn-sm" to={paths.legalPersonsNew}>
            Добавить организацию
        </Link>
    );

    render() {
        return (
            <div className="page-content">
                <Transition dir="full">
                    <div className="page-book__list book-list list book-list--nofilter">
                        {this.renderHead()}
                        <div className="container-fluid my-2">{this.renderActions()}</div>
                        {this.renderListComponent()}
                    </div>
                </Transition>
            </div>
        );
    }
}

export default tableContainer(LegalPersons, "legal-persons");
